<template>
    <div class="price-tag-preset-selector">
        <div class="price-tag-preset-selector__header">
            <span v-if="!previewOnly" class="price-tag-preset-selector__title">
                {{ toSentenceCase($t(`${PRICE_TAGS_I18N_BASE}.gallery`)) }}
            </span>
            <span
                v-if="!previewOnly && !selectedPriceTagId"
                class="price-tag-preset-selector__hint"
            >
                {{ toSentenceCase($t(`${PRICE_TAGS_I18N_BASE}.custom`)) }}
            </span>
            <v-btn
                variant="text"
                color="primary"
                size="small"
                prepend-icon="mdi-eye-outline"
                data-id-e2e="price-tag-preview"
                @click="preview.open()"
            >
                {{ toSentenceCase($t(`${PRICE_TAGS_I18N_BASE}.preview`)) }}
            </v-btn>
        </div>
        <template v-if="!previewOnly">
            <price-tag-gallery
                :items="galleryItems"
                :selected-id="selectedPresetId"
                :disabled="disabled"
                :context-source="contextSource"
                @select="emit('select', $event)"
            />
        </template>
        <price-tag-preview-dialog
            ref="preview"
            :preset="selectedPreset"
            :offer-mechanic="offerMechanic"
            :display-values="displayValues"
            :context-source="contextSource"
        />
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { get } from 'lodash';
import { toSentenceCase } from '@/js/utils/string-utils';
import { PRICE_TAGS_I18N_BASE } from '@/js/composables/usePriceTagText';
import offerMechanicTemplates from '@enums/offer-mechanic-templates';
import { DEFAULT_PRICE_TAG, resolvePriceTagPreset } from '@/js/utils/price-tags/price-tag-presets';
import i18n from '@/js/vue-i18n';

const props = defineProps({
    offerMechanic: {
        type: Object,
        required: true,
    },
    priceTag: {
        type: Object,
        default: null,
    },
    contextSource: {
        type: Object,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    previewOnly: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['select']);

const store = useStore();

const preview = ref(null);

const priceTagPresets = computed(() => store.getters['offerMechanicPresets/priceTagPresets']);
const priceTagsKeyed = computed(() => store.getters['priceTags/priceTagsKeyed']);
const priceTagIdsByOfferTemplate = computed(
    () => store.getters['offerMechanicPresets/priceTagIdsByOfferTemplate']
);

const getPresetLabel = preset => {
    if (preset.name && preset.name !== preset.offerTemplate) return preset.name;
    const translationKey = `planning.promotionsMaintenance.offer.offerMechanic.templates.${
        preset.offerTemplate
    }`;
    const translation = i18n.global.t(translationKey, {
        currencySymbol: props.contextSource.currencySymbol,
    });
    return translation !== translationKey ? translation : preset.name;
};

const galleryItems = computed(() =>
    priceTagPresets.value
        .filter(preset => preset.priceTag)
        .map(preset => ({
            id: preset.offerTemplate,
            label: getPresetLabel(preset),
            priceTag: preset.priceTag,
            offerMechanic: preset.templateStructure,
            displayValues: preset.displayValues,
        }))
);

const selectedPreset = computed(
    () =>
        resolvePriceTagPreset({
            priceTag: props.priceTag,
            offerMechanic: props.offerMechanic,
            priceTagsKeyed: priceTagsKeyed.value,
            priceTagIdsByOfferTemplate: priceTagIdsByOfferTemplate.value,
            defaultPresetId: offerMechanicTemplates.newPricePerItem,
        }) || {
            priceTagId: offerMechanicTemplates.newPricePerItem,
            priceTag: DEFAULT_PRICE_TAG,
        }
);

const selectedPriceTagId = computed(() => selectedPreset.value.priceTagId);
const selectedPresetId = computed(() => props.offerMechanic?.uiFields?.offerTemplate || null);

const displayValues = computed(() => get(props.priceTag, 'displayValues') || {});
</script>

<style lang="scss" scoped>
.price-tag-preset-selector {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    padding: 0.8rem 0;

    &__header {
        display: flex;
        align-items: center;
        gap: 1.6rem;
    }

    &__title {
        font-weight: 600;
    }

    &__hint {
        font-style: italic;
    }
}
</style>
